import React from 'react';
import Button from '@/components/Button';
import { FiArrowLeft, FiArrowRight } from '@/components/react-icons/main';

const generatePaginationButtons = (currentPage, totalPages, maxVisible = 7) => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const buttons = [];
  const halfVisible = Math.floor((maxVisible - 3) / 2);

  buttons.push(1);

  let start = Math.max(2, currentPage - halfVisible);
  let end = Math.min(totalPages - 1, currentPage + halfVisible);

  if (currentPage <= halfVisible + 2) {
    end = Math.min(totalPages - 1, maxVisible - 2);
  } else if (currentPage >= totalPages - halfVisible - 1) {
    start = Math.max(2, totalPages - maxVisible + 3);
  }

  if (start > 2) {
    buttons.push('...');
  }

  for (let i = start; i <= end; i++) {
    if (i > 1 && i < totalPages) {
      buttons.push(i);
    }
  }

  if (end < totalPages - 1) {
    buttons.push('...');
  }

  if (totalPages > 1) {
    buttons.push(totalPages);
  }

  return buttons;
};

const Pagination = ({ currentPage = 1, totalPages = 1, onPageChange, itemsPerPage = 25, totalItems = 0, className = '', showInfo = true, maxVisible = 7 }) => {
  if (totalPages <= 1) {
    return null;
  }

  const buttons = generatePaginationButtons(currentPage, totalPages, maxVisible);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handlePrevious = () => {
    handlePageChange(currentPage - 1);
  };

  const handleNext = () => {
    handlePageChange(currentPage + 1);
  };

  return (
    <div className={`comp-pagination ${className}`}>
      <div className="pagination-content">
        {showInfo && (
          <div className="pagination-info">
            <span className="pagination-text">
              Showing {startItem} to {endItem} of {totalItems} results
            </span>
          </div>
        )}

        <div className="pagination-controls">
          <Button className="ghost pagination-nav" size="sm" icon={FiArrowLeft} disabled={currentPage === 1} onClick={handlePrevious} data-title="Previous page" />

          <div className="pagination-numbers">
            {buttons.map((button, index) => (
              <React.Fragment key={`${button}-${index}`}>
                {button === '...' ? (
                  <span className="pagination-ellipsis">...</span>
                ) : (
                  <Button className={`ghost pagination-number ${currentPage === button ? 'active' : ''}`} size="sm" onClick={() => handlePageChange(button)} disabled={button === '...'}>
                    {button}
                  </Button>
                )}
              </React.Fragment>
            ))}
          </div>

          <Button className="ghost pagination-nav" size="sm" icon={FiArrowRight} disabled={currentPage === totalPages} onClick={handleNext} data-title="Next page" />
        </div>
      </div>
    </div>
  );
};

export default Pagination;
